import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { DecideDisputeRequest, DecideDisputeResponse, InternalDispute } from "../../api/staffTypes";
import { Banner, Button, Card, EmptyState, Input, Money, PageHeader, QueryView, RadioGroup, StatusPill, Table, Textarea } from "../../components/ui";
import { CursorListView } from "../../components/CursorListView";
import { staffKeys } from "../../layout/StaffShell";
import { amountValidationMessage, isZeroDecimal, subtractDecimal, validateAmountInput } from "../../lib/money";
import { useCursorList } from "../../lib/useCursorList";
import {
  AwaitingSecondBanner,
  DUAL_CONTROL_THRESHOLD,
  DualControlProgress,
  FilterTabs,
  MoneyConfirmDialog,
  needsTwoReviewers,
  reasonError,
  shortId,
  StaffRejection,
  StepUpNote,
  useStaffCommand,
  when,
} from "./staffCommon";

// The reviewer side of statement-line disputes (0063; disputes.go:84-91).
// The queue (?state=open is oldest first), a dispute's internal view
// (statement_line_dispute_json(p_internal := true): votes, the
// reservation's state), and the decision: RequireRecentMFA, idempotent,
// a single deny resolves, an uphold that moves more than 100 needs two
// distinct reviewers and agrees on one amount (0063:1013-1206).

type StateFilter = "open" | "resolved" | "all";

function outcomePill(d: InternalDispute) {
  if (d.state === "open") return <StatusPill tone="info">Open</StatusPill>;
  if (d.outcome === "upheld") return <StatusPill tone="success">Upheld · {d.resolution?.replace(/_/g, " ")}</StatusPill>;
  return <StatusPill tone="neutral">Denied</StatusPill>;
}

function upholdVotes(d: InternalDispute) {
  return d.votes.filter((v) => v.decision === "uphold");
}

export function StaffDisputesPage() {
  const ep = useEndpoints();
  const [params, setParams] = useSearchParams();
  const state = (params.get("state") as StateFilter | null) ?? "open";
  const list = useCursorList(staffKeys.disputes(state), (cursor) => ep.staff.disputes.list(state, cursor), (p) => p.disputes);

  return (
    <div className="stack-lg">
      <PageHeader title="Statement disputes" description="Open disputes are oldest first; resolved and all are newest first." />
      <FilterTabs
        label="Dispute state"
        value={state}
        onChange={(s) => setParams({ state: s })}
        options={[
          { value: "open", label: "Open" },
          { value: "resolved", label: "Resolved" },
          { value: "all", label: "All" },
        ]}
      />
      <CursorListView list={list} loadingLabel="Loading disputes" empty={<EmptyState title="No disputes here" />}>
        {(rows) => (
          <Table
            caption="Disputes"
            rows={rows}
            rowKey={(d) => d.dispute_id}
            columns={[
              { key: "id", header: "Dispute", cell: (d) => <Link to={`/staff/disputes/${d.dispute_id}`}>{shortId(d.dispute_id)}</Link> },
              { key: "kind", header: "Kind", cell: (d) => (d.kind === "advertiser_billing" ? "Advertiser billing" : "Publisher revenue share") },
              { key: "filed", header: "Filed", cell: (d) => when(d.filed_at) },
              { key: "line", header: "Line amount", numeric: true, cell: (d) => <Money value={d.line.amount} currency={d.line.currency} /> },
              { key: "votes", header: "Uphold votes", numeric: true, cell: (d) => `${upholdVotes(d).length}${d.dual_approval_required ? " / 2" : ""}` },
              { key: "state", header: "State", cell: outcomePill },
            ]}
          />
        )}
      </CursorListView>
    </div>
  );
}

export function StaffDisputeDetailPage() {
  const { disputeId = "" } = useParams();
  const ep = useEndpoints();
  const q = useQuery({ queryKey: staffKeys.dispute(disputeId), queryFn: () => ep.staff.disputes.get(disputeId) });
  return (
    <div className="stack-lg">
      <PageHeader title={`Dispute ${shortId(disputeId)}`} back={{ to: "/staff/disputes", label: "Disputes" }} />
      <QueryView query={q} loadingLabel="Loading the dispute">
        {(d) => <DisputeDetail d={d} />}
      </QueryView>
    </div>
  );
}

function DisputeDetail({ d }: { d: InternalDispute }) {
  const ep = useEndpoints();
  const upholds = upholdVotes(d);
  const required = d.dual_approval_required ? 2 : 1;
  return (
    <div className="stack">
      <Card title="The disputed line" actions={outcomePill(d)}>
        <dl className="definition-list">
          <dt>Kind</dt>
          <dd>{d.kind === "advertiser_billing" ? "Advertiser billing" : "Publisher revenue share"}</dd>
          <dt>Party / filed by</dt>
          <dd className="mono">
            {d.party_id} / {d.filed_by}
          </dd>
          <dt>Statement line</dt>
          <dd>
            Line {d.line.line_no} · {d.line.account_type.replace(/_/g, " ")} · <Money value={d.line.amount} currency={d.line.currency} /> ·{" "}
            {d.line.description}
          </dd>
          <dt>Delivery</dt>
          <dd>{d.reservation_id ? <><span className="mono">{d.reservation_id}</span> · {d.reservation_state?.replace(/_/g, " ") ?? "unknown"}</> : "Not tied to a delivery"}</dd>
          <dt>Customer's reason</dt>
          <dd>
            <pre className="staff-inert">{d.reason}</pre>
          </dd>
          <dt>Filed / window closes</dt>
          <dd>
            {when(d.filed_at)} / {when(d.dispute_eligible_until)}
          </dd>
          {d.state === "resolved" ? (
            <>
              <dt>Resolution</dt>
              <dd>
                {d.resolution?.replace(/_/g, " ")} {d.amount && !isZeroDecimal(d.amount) ? <>· <Money value={d.amount} currency={d.currency} /></> : null} ·{" "}
                {when(d.decided_at)}
              </dd>
              <dt>Note</dt>
              <dd>{d.resolution_note ?? "—"}</dd>
              <dt>Amendment</dt>
              <dd className="mono">{d.amendment_id ?? "Not issued yet (the statement job issues it)"}</dd>
            </>
          ) : null}
        </dl>
      </Card>
      <Card title="Evidence">
        {d.evidence.length ? (
          <ul className="stack-sm">
            {d.evidence.map((e) => (
              <li key={e.evidence_id}>
                <span className="mono">{shortId(e.evidence_id)}</span> · {e.media_type} · {Math.ceil(e.byte_size / 1024)} KB · {e.scan_state}{" "}
                {e.scan_state === "clean" ? (
                  <a href={ep.staff.disputes.evidenceUrl(e.evidence_id)} download>
                    Download
                  </a>
                ) : (
                  <span className="muted small">(not downloadable until clean)</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">No evidence attached.</p>
        )}
        <p className="xs muted">Files are served as attachments; open them outside your authenticated browser if you're unsure of them.</p>
      </Card>
      <Card title="Votes">
        <DualControlProgress
          label="Uphold approvals"
          approvals={d.state === "resolved" && d.outcome === "upheld" ? Math.max(upholds.length, required) : upholds.length}
          required={required}
          votes={d.votes}
        />
      </Card>
      {d.state === "open" ? <DisputeDecision d={d} /> : null}
    </div>
  );
}

/** What an uphold would move, and its effect, derived from what 0063's decide reads. */
function upholdPlan(d: InternalDispute, credit: string | null): { amount: string; effect: ReactNode; moves: boolean } {
  if (d.kind === "advertiser_billing") {
    if (d.reservation_state === "reversed") {
      return { amount: "0", moves: false, effect: "A safety finding already reversed this delivery, so the uphold moves nothing (resolution: already reversed)." };
    }
    // v_amount := -line_amount (0063:1098): the line is the capture, negative.
    const amount = subtractDecimal("0", d.line.amount);
    return {
      amount,
      moves: true,
      effect: (
        <>
          Reverses this delivery: <Money value={amount} currency={d.line.currency} /> goes back to the advertiser's wallet through the
          campaign reservation account, and the publisher's share and the platform fee are taken back. It shows up as an amendment of
          this statement.
        </>
      ),
    };
  }
  if (!d.reservation_id) {
    return {
      amount: "0",
      moves: false,
      effect: "This line isn't tied to a delivery, so an uphold moves no money: it resolves as manual follow-up, and your reason is the note for whoever makes the adjustment.",
    };
  }
  const amount = credit ?? "0";
  return {
    amount,
    moves: true,
    effect: (
      <>
        Credits <Money value={amount} currency={d.currency} /> to the publisher's payable balance against this delivery. The platform
        funds it (chargeback loss expense), capped at the publisher's remaining share.
      </>
    ),
  };
}

function DisputeDecision({ d }: { d: InternalDispute }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<"uphold" | "deny" | "">("");
  const [reason, setReason] = useState("");
  const proposed = upholdVotes(d).length && d.amount ? d.amount : null;
  const needsCredit = d.kind === "publisher_revenue_share" && !!d.reservation_id;
  const [credit, setCredit] = useState(proposed ?? "");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<DecideDisputeRequest | null>(null);
  const cmd = useStaffCommand(
    (req: DecideDisputeRequest, opts) => ep.staff.disputes.decide(d.dispute_id, req, opts),
    (res: DecideDisputeResponse) => {
      setConfirming(null);
      qc.setQueryData(staffKeys.dispute(d.dispute_id), res.dispute);
      void qc.invalidateQueries({ queryKey: ["staff", "disputes"] });
      void qc.invalidateQueries({ queryKey: staffKeys.disputeCount });
    },
  );

  const creditValue = needsCredit ? (proposed ?? credit.trim()) : null;
  const plan = upholdPlan(d, creditValue);
  const alreadyUpheld = upholdVotes(d).length;
  const twoNeeded = plan.moves && needsTwoReviewers(plan.amount);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose uphold or deny.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    const req: DecideDisputeRequest = { decision, reason: reason.trim() };
    if (decision === "uphold" && needsCredit) {
      const v = validateAmountInput(creditValue ?? "", d.currency);
      if (!v.ok) return setFormError(amountValidationMessage(v.reason, d.currency));
      req.credit_amount = v.value;
    }
    setFormError(null);
    cmd.reset();
    // A deny moves no money and an uphold that moves nothing needs no money confirmation.
    if (decision === "uphold" && plan.moves) setConfirming(req);
    else cmd.run(req);
  }

  const res = cmd.data;
  return (
    <Card title="Decision">
      {res?.result === "awaiting_second_approval" ? <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <RadioGroup
          legend="Decision"
          name="dispute-decision"
          value={decision}
          onChange={setDecision}
          options={[
            { value: "uphold", label: "Uphold", hint: `Moves money. More than ${DUAL_CONTROL_THRESHOLD} needs a second, distinct reviewer.` },
            { value: "deny", label: "Deny", hint: "One reviewer resolves it; no money moves." },
          ]}
        />
        {decision === "uphold" && needsCredit ? (
          <Input
            label={`Credit to the publisher (${d.currency.toUpperCase()})`}
            inputMode="decimal"
            value={proposed ?? credit}
            disabled={!!proposed}
            onChange={(e) => setCredit(e.target.value)}
            hint={proposed ? "The first reviewer's amount. Every uphold vote has to agree on it." : "Capped at the publisher's remaining share of this delivery."}
          />
        ) : null}
        {decision === "uphold" ? (
          <p className="small">
            {plan.effect} {twoNeeded ? <strong>{alreadyUpheld ? "This is the second vote it needs." : "It needs two distinct reviewers."}</strong> : null}
          </p>
        ) : null}
        <Textarea label="Reason" hint="1–2,000 characters. The customer sees it as the resolution note." value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" loading={cmd.isPending && !confirming}>
            Record decision
          </Button>
        </div>
      </form>
      <MoneyConfirmDialog
        open={!!confirming}
        title="Uphold this dispute?"
        amount={plan.amount}
        currency={d.kind === "advertiser_billing" ? d.line.currency : d.currency}
        effect={plan.effect}
        pendingSecond={twoNeeded && alreadyUpheld === 0 && `More than ${DUAL_CONTROL_THRESHOLD} ${d.currency.toUpperCase()} moves.`}
        confirmLabel={twoNeeded && alreadyUpheld === 0 ? "Record uphold vote" : "Uphold and move the money"}
        busy={cmd.isPending}
        error={confirming ? cmd.error : undefined}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && cmd.run(confirming)}
      />
    </Card>
  );
}
